
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getTags,
  createTag,
  updateTag,
  deleteTag,
  getProjectTags,
  assignTagToProject,
  removeTagFromProject
} from './database-tags';
import { ProjectTag } from './types/database-types';
import { useToast } from '@/hooks/errors/use-toast';

// Query key factory for tags
export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  list: () => [...tagKeys.lists()] as const,
  projectTags: () => [...tagKeys.all, 'projectTags'] as const,
  projectTag: (projectId: string) => [...tagKeys.projectTags(), projectId] as const,
};

/**
 * Hook for fetching all tags
 */
export function useTagsQuery() {
  return useQuery({
    queryKey: tagKeys.list(),
    queryFn: getTags,
  });
}

/**
 * Hook for fetching tags associated with a project
 * Works for both projects and pages (pages are treated as projects in the backend)
 */
export function useProjectTagsQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: tagKeys.projectTag(projectId || ''),
    queryFn: () => projectId ? getProjectTags(projectId) : Promise.resolve([]),
    enabled: !!projectId,
  });
}

// Re-export with page name for clarity when used with pages
export { useProjectTagsQuery as usePageTagsQuery };

/**
 * Hook for tag mutations (create, update, delete) with optimistic updates
 */
export function useTagMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const createTagMutation = useMutation({
    mutationFn: (tag: Omit<ProjectTag, 'id' | 'createdAt' | 'userId'>) => createTag(tag),
    onMutate: async (newTag) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tagKeys.list() });
      
      // Snapshot the previous value
      const previousTags = queryClient.getQueryData<ProjectTag[]>(tagKeys.list());
      
      // Optimistically update to the new value
      const optimisticTag: ProjectTag = {
        ...newTag,
        id: `temp-${Date.now()}`, // Temporary ID
        userId: 'temp-user', // Temporary userId for optimistic update
        createdAt: new Date().toISOString(),
      };
      
      queryClient.setQueryData<ProjectTag[]>(tagKeys.list(), (old) => [
        ...(old || []),
        optimisticTag,
      ]);
      
      // Return a context object with the snapshotted value
      return { previousTags, optimisticTag };
    },
    onError: (err, newTag, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTags) {
        queryClient.setQueryData(tagKeys.list(), context.previousTags);
      }
      console.error('Error creating tag:', err);
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    },
    onSuccess: (data, variables, context) => {
      // Replace the optimistic tag with the real one
      queryClient.setQueryData<ProjectTag[]>(tagKeys.list(), (old) => 
        (old || []).map(tag => 
          tag.id === context?.optimisticTag.id ? data : tag
        )
      );
      
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the correct data
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
  
  const updateTagMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<ProjectTag> }) => updateTag(id, updates),
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: tagKeys.list() });
      
      const previousTags = queryClient.getQueryData<ProjectTag[]>(tagKeys.list());
      
      queryClient.setQueryData<ProjectTag[]>(tagKeys.list(), (old) =>
        (old || []).map(tag => tag.id === id ? { ...tag, ...updates } : tag)
      );
      
      return { previousTags };
    },
    onError: (err, variables, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(tagKeys.list(), context.previousTags);
      }
      console.error('Error updating tag:', err);
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.projectTags() });
    },
  });
  
  const deleteTagMutation = useMutation({
    mutationFn: deleteTag,
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: tagKeys.list() });
      
      const previousTags = queryClient.getQueryData<ProjectTag[]>(tagKeys.list());
      
      queryClient.setQueryData<ProjectTag[]>(tagKeys.list(), (old) =>
        (old || []).filter(tag => tag.id !== deletedId)
      );
      
      return { previousTags };
    },
    onError: (err, variables, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(tagKeys.list(), context.previousTags);
      }
      console.error('Error deleting tag:', err);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.projectTags() });
    },
  });
  
  return {
    createTag: createTagMutation.mutate,
    updateTag: updateTagMutation.mutate,
    deleteTag: deleteTagMutation.mutate,
    isPending: 
      createTagMutation.isPending || 
      updateTagMutation.isPending || 
      deleteTagMutation.isPending
  };
}

/**
 * Hook for project-tag assignment mutations
 */
export function useProjectTagAssignmentMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const assignTagMutation = useMutation({
    mutationFn: ({ tagId, projectId }: { tagId: string, projectId: string }) => assignTagToProject(tagId, projectId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: tagKeys.projectTag(projectId) });
      toast({
        title: "Success",
        description: "Tag assigned to project",
      });
    },
    onError: (error) => {
      console.error('Error assigning tag to project:', error);
      toast({
        title: "Error",
        description: "Failed to assign tag to project",
        variant: "destructive",
      });
    },
  });
  
  const removeTagMutation = useMutation({
    mutationFn: ({ tagId, projectId }: { tagId: string, projectId: string }) => removeTagFromProject(tagId, projectId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: tagKeys.projectTag(projectId) });
      toast({
        title: "Success",
        description: "Tag removed from project",
      });
    },
    onError: (error) => {
      console.error('Error removing tag from project:', error);
      toast({
        title: "Error",
        description: "Failed to remove tag from project",
        variant: "destructive",
      });
    },
  });
  
  return {
    assignTag: assignTagMutation.mutate,
    removeTag: removeTagMutation.mutate,
    isPending: assignTagMutation.isPending || removeTagMutation.isPending
  };
}
